import React from 'react';
import { ChevronRight, Code, Zap, ArrowRight, TrendingUp, Target, Lightbulb, Star, Rocket } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import './App.css';
import './components/CTASections.css';
import { initGA, trackPageView, useScrollTracking, useTimeOnPageTracking, WHATSAPP_URL, waLink, trackCTAClick } from './analytics';
import CTAButton from './components/CTAButton';

function App() {
  const location = useLocation();
  
  // Initialize GA and track page views
  React.useEffect(() => {
    initGA();
    trackPageView(location.pathname + location.search + location.hash);
  }, [location]);
  
  // Enable scroll and time tracking
  useScrollTracking();
  useTimeOnPageTracking();
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Offset untuk fixed header
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'auto' // Menggunakan auto untuk scroll lebih cepat
      });
    }
  };

  return (
    <div className="app">
      <main>
        {/* Hero Section - Clean & Elegant */}
        <section id="home" className="hero">
          <div className="hero-container">
            <div className="hero-content">
              <div className="hero-badge">
                <span className="badge-text">🏆 Digital Transformation Partner</span>
              </div>
              <h1 className="hero-title">
                Solusi Transformasi Digital Berbasis AI, Cloud & Managed IT Services
              </h1>
              <p className="hero-subtitle">
                <strong>Partner Transformasi Digital Terpercaya</strong> - Solusi AI, Cloud Infrastructure, Managed Services, Cyber Security, dan Business Automation untuk pertumbuhan bisnis berkelanjutan. Gratis Konsultasi!
              </p>
              <div className="hero-actions">
                <CTAButton
                  variant="primary"
                  size="large"
                  href={WHATSAPP_URL}
                  isWhatsApp={true}
                  trackingLabel="hero_cta"
                  subtext="Respon cepat < 5 menit"
                >
                  Konsultasi Transformasi Digital
                </CTAButton>
                <CTAButton
                  variant="secondary"
                  size="large"
                  onClick={() => {
                    trackCTAClick('Lihat Portfolio', location.pathname + location.search + location.hash);
                    scrollToSection('portfolio');
                  }}
                  trackingLabel="Lihat Portfolio"
                >
                  📊 Lihat Portfolio
                </CTAButton>
              </div>
            </div>
            
            <div className="hero-visual">
              <div className="hero-graphic">
                <div className="floating-card">
                  <div className="card-icon">
                    <Rocket size={32} />
                  </div>
                  <div className="card-title">Innovation</div>
                </div>
                <div className="floating-card">
                  <div className="card-icon">
                    <Target size={32} />
                  </div>
                  <div className="card-title">Precision</div>
                </div>
                <div className="floating-card">
                  <div className="card-icon">
                    <Star size={32} />
                  </div>
                  <div className="card-title">Excellence</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="section section-enhanced section-divider">
          <div className="section-container">
            <header className="section-header">
              <div className="section-badge">Company Overview</div>
              <h2 className="section-title">Mitra Transformasi Digital untuk Enterprise Indonesia</h2>
              <p className="section-subtitle">
                Cakra Digital Innovation adalah perusahaan Technology Solutions & Digital Transformation yang fokus membantu enterprise Indonesia bertransformasi melalui solusi AI, Cloud, Managed IT Services, dan Cyber Security yang inovatif dan berkelanjutan.
              </p>
            </header>
            
            <div className="about-content">
              <div className="about-text">
                <p className="about-description">
                  Kami bertransformasi menjadi perusahaan Technology Solutions dengan visi menjadi partner transformasi digital terdepan di Indonesia. Dengan komitmen jangka panjang, kami menghadirkan solusi enterprise-grade yang menggabungkan AI, Cloud, Automation, dan Security untuk mempercepat pertumbuhan bisnis Anda.
                </p>
                
                <div className="about-values">
                  <div className="value-item">
                    <div className="value-icon">
                      <Lightbulb size={24} />
                    </div>
                    <div className="value-content">
                      <h4>Enterprise Quality</h4>
                      <p>Solusi dengan standar enterprise, security by design, dan kualitas terjamin untuk skalabilitas dan keandalan bisnis Anda.</p>
                    </div>
                  </div>
                  
                  <div className="value-item">
                    <div className="value-icon">
                      <Target size={24} />
                    </div>
                    <div className="value-content">
                      <h4>AI-First Solutions</h4>
                      <p>Solusi berbasis AI dan automation untuk efisiensi operasional, pengambilan keputusan cerdas, dan competitive advantage.</p>
                    </div>
                  </div>
                  
                  <div className="value-item">
                    <div className="value-icon">
                      <TrendingUp size={24} />
                    </div>
                    <div className="value-content">
                      <h4>Cloud-Native Architecture</h4>
                      <p>Infrastruktur cloud modern yang scalable, secure, dan cost-effective untuk mendukung pertumbuhan bisnis Anda.</p>
                    </div>
                  </div>
                  
                  <div className="value-item">
                    <div className="value-icon">
                      <Star size={24} />
                    </div>
                    <div className="value-content">
                      <h4>Security by Design</h4>
                      <p>Keamanan enterprise-grade dengan compliance standar internasional untuk melindungi aset digital bisnis Anda.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="section section-enhanced section-divider">
          <div className="section-container">
            <header className="section-header">
              <div className="section-badge">Layanan Utama</div>
              <h2 className="section-title">Solusi Transformasi Digital Enterprise</h2>
              <p className="section-subtitle">
                Layanan komprehensif AI, Cloud, Managed Services, dan Security untuk transformasi digital end-to-end.
              </p>
            </header>
            
            <div className="services-grid">
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <Code className="service-icon" size={32} />
                </div>
                <div className="service-content">
                  <h3 className="service-title">AI & Automation Solutions</h3>
                  <p className="service-description">
                    Solusi AI-powered untuk business automation, predictive analytics, dan intelligent process automation.
                  </p>
                  <ul className="service-features">
                    <li>Machine Learning & AI Models</li>
                    <li>Robotic Process Automation (RPA)</li>
                    <li>Intelligent Document Processing</li>
                    <li>AI-Powered Analytics</li>
                  </ul>
                  <Link 
                    to="/pricing"
                    className="service-link"
                  >
                    <span>Pelajari AI Solutions</span>
                    <ChevronRight size={16} />
                  </Link>
                </div>
              </div>
              
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <Target className="service-icon" size={32} />
                </div>
                <div className="service-content">
                  <h3 className="service-title">Cloud Infrastructure</h3>
                  <p className="service-description">
                    Solusi cloud-native untuk modernisasi infrastruktur, cloud migration, dan multi-cloud management.
                  </p>
                  <div className="service-price">Custom Quote</div>
                  <ul className="service-features">
                    <li>Cloud Migration (AWS, GCP, Azure)</li>
                    <li>Multi-Cloud Management</li>
                    <li>DevOps & SRE</li>
                    <li>Cloud Cost Optimization</li>
                  </ul>
                  <a 
                    href={waLink('Halo, saya ingin konsultasi Cloud Infrastructure. Mohon info lebih lanjut.')}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="service-link"
                  >
                    <span>Konsultasi Cloud</span>
                    <ChevronRight size={16} />
                  </a>
                </div>
              </div>
              
              <div className="service-card">
                <div className="service-icon-wrapper">
                  <Zap className="service-icon" size={32} />
                </div>
                <div className="service-content">
                  <h3 className="service-title">Managed IT Services</h3>
                  <p className="service-description">
                    Layanan managed services komprehensif untuk IT infrastructure, security operations, dan application management.
                  </p>
                  <div className="service-price">Custom Quote</div>
                  <ul className="service-features">
                    <li>24/7 IT Infrastructure Management</li>
                    <li>Security Operations Center (SOC)</li>
                    <li>Application Management</li>
                    <li>Incident Response & Support</li>
                  </ul>
                  <Link to="/website-maintenance" className="service-link">
                    <span>Pelajari Managed Services</span>
                    <ChevronRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section id="process" className="section section-enhanced section-divider">
          <div className="section-container">
            <header className="section-header">
              <div className="section-badge">Proses Kerja</div>
              <h2 className="section-title">Metodologi Transformasi Digital Terstruktur</h2>
              <p className="section-subtitle">
                Framework digital transformation kami memastikan implementasi yang systematic, measurable, dan sustainable.
              </p>
            </header>
            
            <div className="process-timeline">
              <div className="process-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Discovery & Assessment</h3>
                  <p>Assessment digital maturity, analisis gap, dan identifikasi opportunity untuk transformasi digital yang tepat.</p>
                </div>
              </div>
              
              <div className="process-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Strategy & Roadmap</h3>
                  <p>Develop digital transformation strategy dengan technology roadmap dan business case yang jelas.</p>
                </div>
              </div>
              
              <div className="process-step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Implementation & Deployment</h3>
                  <p>Implementasi solusi dengan enterprise-grade quality, security by design, dan change management.</p>
                </div>
              </div>
              
              <div className="process-step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Optimization & Scale</h3>
                  <p>Continuous optimization, performance monitoring, dan scaling untuk sustainable business impact.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Portfolio Section */}
        <section id="portfolio" className="section section-enhanced section-divider">
          <div className="section-container">
            <header className="section-header">
              <div className="section-badge">Portfolio</div>
              <h2 className="section-title">Proyek Transformasi Digital</h2>
              <p className="section-subtitle">
                Lihat bagaimana kami membantu enterprise Indonesia bertransformasi dengan solusi AI, Cloud, dan Managed Services.
              </p>
            </header>
            
            <div className="portfolio-grid">
              <div className="portfolio-item">
                <div className="portfolio-image">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/portofolio/Portofolio.png`}
                    alt="Business Website"
                    loading="lazy"
                  />
                  <div className="portfolio-overlay">
                    <div className="portfolio-content">
                      <h3>Enterprise Digital Platform</h3>
                      <p>AI-powered digital platform for enterprise client</p>
                      <button className="portfolio-link">View Project</button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="portfolio-item">
                <div className="portfolio-image">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/portofolio/medius-Quran.jpeg`}
                    alt="Digital Marketing"
                    loading="lazy"
                  />
                  <div className="portfolio-overlay">
                    <div className="portfolio-content">
                      <h3>AI Automation Solution</h3>
                      <p>Intelligent automation and business process optimization</p>
                      <a 
                        href="http://medius-quran.gt.tc/?i=1" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="portfolio-link"
                      >
                        View Project
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Statistics Section */}
        <section id="stats" className="section section-enhanced section-divider">
          <div className="section-container">
            <header className="section-header">
              <div className="section-badge">Kinerja Kami</div>
              <h2 className="section-title">Enterprise Quality & Client Success</h2>
              <p className="section-subtitle">
                Komitmen kami terhadap kualitas enterprise, security by design, dan kepuasan klien adalah prioritas utama dalam setiap transformasi digital.
              </p>
            </header>
            
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-number">100%</div>
                <div className="stat-label">Klien Puas</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">1+</div>
                <div className="stat-label">Tahun Pengalaman</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">8+</div>
                <div className="stat-label">Divisi Layanan</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">Resmi</div>
                <div className="stat-label">NIB 2005260092859 Terdaftar</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section section-enhanced">
          <div className="cta-container">
            <div className="cta-content">
              <h2 className="cta-title">Siap Transformasi Digital untuk Pertumbuhan Berkelanjutan?</h2>
              <p className="cta-subtitle">
                Mari diskusikan bagaimana solusi AI, Cloud, dan Managed Services kami dapat mempercepat transformasi digital bisnis Anda.
              </p>
              <div className="cta-actions">
                <a 
                  href={WHATSAPP_URL} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-primary btn-large btn-glow"
                >
                  <span>Mulai Transformasi Digital</span>
                  <ChevronRight size={24} />
                </a>
                <button 
                  onClick={() => scrollToSection('portfolio')}
                  className="btn btn-secondary btn-large"
                >
                  <span>Lihat Solusi Kami</span>
                  <ArrowRight size={24} />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer CTA Section */}
        <section className="footer-cta-section">
          <div className="section-container">
            <div className="footer-cta-content">
              <h2 className="cta-title">Mulai Perjalanan Transformasi Digital Anda</h2>
              <p className="cta-subtitle">
                Konsultasikan kebutuhan transformasi digital Anda GRATIS sekarang
              </p>
              <CTAButton
                variant="whatsapp"
                size="large"
                href={WHATSAPP_URL}
                isWhatsApp={true}
                trackingLabel="footer_cta"
                fullWidth={true}
              >
                Chat WhatsApp Sekarang
              </CTAButton>
            </div>
          </div>
        </section>

      </main>
    </div>
  );
}

export default App;
